/**
 * Main Layout Component - Vertical Layout
 * 響應式設計支援三種解析度:
 * - Desktop: >1200px (顯示完整側邊欄)
 * - Tablet: 768-1200px (可折疊側邊欄)
 * - Mobile: <768px (抽屜式側邊欄)
 */
import { useState, useEffect, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Drawer, Button, theme, Grid, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  DashboardOutlined,
  ProjectOutlined,
  LineChartOutlined,
  FileTextOutlined,
  SettingOutlined,
  MenuOutlined,
  LogoutOutlined,
  UserOutlined,
  TeamOutlined,
  BankOutlined,
  KeyOutlined,
  ExperimentOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '@/store/authStore';
import organizationService, { type Organization } from '@/api/services/organizationService';
import ChangePasswordModal from '@/components/password/ChangePasswordModal';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import type { MenuProps } from 'antd';

const { Header, Sider, Content, Footer } = Layout;
const { useBreakpoint } = Grid;

type MenuItem = Required<MenuProps>['items'][number];

// 角色顯示名稱和顏色配置
const getRoleDisplay = (role: string) => {
  const roleMap: Record<string, { label: string; color: string }> = {
    system_admin: { label: 'System Admin', color: 'red' },
    org_admin: { label: 'Org Admin', color: 'volcano' },
    project_manager: { label: 'Project Manager', color: 'blue' },
    data_entry: { label: 'Data Entry', color: 'green' },
    viewer: { label: 'Viewer', color: 'default' },
  };
  return roleMap[role] || { label: role, color: 'default' };
};

const MainLayout = () => {
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, clearAuth } = useAuthStore();
  const screens = useBreakpoint();
  const { token } = theme.useToken();

  // 響應式狀態
  const [collapsed, setCollapsed] = useState(true);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);

  // 判斷當前設備類型
  const isMobile = !screens.md; // <768px
  const isTablet = screens.md && !screens.xl; // 768-1200px
  const isDesktop = screens.xl; // >1200px

  // 根據設備類型設置側邊欄寬度
  const siderWidth = isDesktop ? 240 : 200;
  const collapsedWidth = isMobile ? 0 : 80;

  // 移動端自動關閉側邊欄
  useEffect(() => {
    if (isMobile) {
      setCollapsed(true);
    } else {
      setCollapsed(false);
    }
  }, [isMobile]);

  // 載入使用者所屬組織資訊
  useEffect(() => {
    const loadOrganization = async () => {
      if (user?.organization_id) {
        try {
          const org = await organizationService.getOrganization(user.organization_id);
          setOrganization(org);
        } catch (error) {
          console.error('載入組織資訊失敗:', error);
        }
      }
    };
    loadOrganization();
  }, [user?.organization_id]);

  // 導航選單項目 - 使用 useMemo 確保語言切換時會重新計算
  const menuItems: MenuItem[] = useMemo(() => [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: t('common:nav.dashboard'),
    },
    {
      key: '/projects',
      icon: <ProjectOutlined />,
      label: t('common:nav.projects'),
      children: [
        {
          key: '/projects/basic-management',
          label: t('common:nav.projectsBasicManagement'),
        },
        {
          key: '/projects/pa-description',
          label: t('common:nav.sectionA'),
        },
        {
          key: '/projects/section-b',
          label: t('common:nav.sectionB'),
        },
        {
          key: '/projects/section-c',
          label: t('common:nav.sectionC'),
        },
        {
          key: '/projects/section-d',
          label: t('common:nav.sectionD'),
        },
        {
          key: '/projects/section-e',
          label: t('common:nav.sectionE'),
        },
        {
          key: '/projects/section-f',
          label: t('common:nav.sectionF'),
        },
        {
          key: '/projects/appendix',
          label: t('common:nav.appendix'),
        },
      ],
    },
    {
      key: '/monitoring',
      icon: <LineChartOutlined />,
      label: t('common:nav.monitoring'),
      children: [
        {
          key: '/monitoring/info',
          label: t('common:nav.monitoringInfo'),
        },
        {
          key: '/monitoring/section-a',
          label: t('common:nav.monitoringSectionA'),
        },
        {
          key: '/monitoring/section-b',
          label: t('common:nav.monitoringSectionB'),
        },
        {
          key: '/monitoring/section-c',
          label: t('common:nav.monitoringSectionC'),
        },
        {
          key: '/monitoring/section-d',
          label: t('common:nav.monitoringSectionD'),
        },
        {
          key: '/monitoring/section-e',
          label: t('common:nav.monitoringSectionE'),
        },
      ],
    },
    {
      key: '/reporting',
      icon: <FileTextOutlined />,
      label: t('common:nav.reporting'),
      children: [
        {
          key: '/reports',
          label: t('common:nav.reports'),
        },
        {
          key: '/monitoring-reports',
          label: t('common:nav.monitoringReports'),
        },
      ],
    },
    {
      key: '/tools',
      icon: <ToolOutlined />,
      label: t('common:nav.tools'),
      children: [
        {
          key: '/settings/ghg-tables',
          icon: <ExperimentOutlined />,
          label: t('common:nav.ghgTables'),
        },
        {
          key: '/settings/boundary-emissions',
          icon: <ExperimentOutlined />,
          label: t('common:nav.boundaryEmissions'),
        },
        {
          key: '/settings/methodology-config',
          icon: <ExperimentOutlined />,
          label: t('common:nav.methodologyConfig'),
        },
        {
          key: '/settings/methodology-explanation',
          icon: <ExperimentOutlined />,
          label: t('common:nav.methodologyExplanation'),
        },
        {
          key: '/settings/sectoral-methodology-rel',
          icon: <ExperimentOutlined />,
          label: t('common:nav.sectoralMethodologyRel'),
        },
      ],
    },
    {
      key: '/settings',
      icon: <SettingOutlined />,
      label: t('common:nav.settings'),
      children: [
        {
          key: '/organizations',
          icon: <BankOutlined />,
          label: t('common:nav.organizations'),
        },
        {
          key: '/users',
          icon: <UserOutlined />,
          label: t('common:nav.users'),
        },
        {
          key: '/settings/system-codes',
          icon: <KeyOutlined />,
          label: t('common:nav.systemCodes'),
        },
      ],
    },
  ], [t]);

  // 選單點擊處理
  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    navigate(key);
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  // 登出處理
  const handleLogout = () => {
    clearAuth();
    navigate('/auth/login');
  };

  // 側邊欄內容
  const siderContent = (
    <>
      {/* Logo 區域 - 包含折疊按鈕 */}
      <div
        style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
          padding: '0 16px',
          position: 'relative',
          background: '#141414',
        }}
      >
        {/* Logo 與折疊按鈕組合 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {!collapsed && (
            <>
              <h1 style={{ margin: 0, fontSize: 20, fontWeight: 'bold', color: '#ff8c00' }}>
                CDM-PDD
              </h1>
              {!isMobile && (
                <Button
                  type="text"
                  icon={<MenuOutlined />}
                  onClick={() => setCollapsed(!collapsed)}
                  style={{
                    fontSize: 16,
                    width: 32,
                    height: 32,
                  }}
                />
              )}
            </>
          )}
          {collapsed && !isMobile && (
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{
                fontSize: 20,
                width: 40,
                height: 40,
              }}
            />
          )}
          {collapsed && isMobile && (
            <span style={{ fontSize: 24, fontWeight: 'bold', color: '#ff8c00' }}>C</span>
          )}
        </div>
      </div>

      {/* 導航選單 - 加入 overflow: auto 支援捲軸 */}
      <Menu
        mode="inline"
        selectedKeys={[location.pathname]}
        items={menuItems}
        onClick={handleMenuClick}
        style={{
          borderRight: 0,
          height: 'calc(100% - 64px)',
          overflow: 'auto',
          background: '#0d1929',
        }}
      />
    </>
  );

  return (
    <Layout style={{ minHeight: '100vh', background: '#051326' }}>
      {/* 桌面/平板側邊欄 */}
      {!isMobile && (
        <Sider
          theme="dark"
          collapsed={collapsed}
          width={siderWidth}
          collapsedWidth={collapsedWidth}
          breakpoint="lg"
          style={{
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            borderRight: `1px solid ${token.colorBorderSecondary}`,
            background: '#0d1929',
          }}
        >
          {siderContent}
        </Sider>
      )}

      {/* 移動端抽屜側邊欄 */}
      {isMobile && (
        <Drawer
          placement="left"
          onClose={() => setMobileDrawerOpen(false)}
          open={mobileDrawerOpen}
          closable={false}
          width={240}
          styles={{ body: { padding: 0 } }}
        >
          {siderContent}
        </Drawer>
      )}

      {/* 主要內容區域 */}
      <Layout
        style={{
          marginLeft: isMobile ? 0 : collapsed ? collapsedWidth : siderWidth,
          transition: 'margin-left 0.2s',
        }}
      >
        {/* 頂部導航欄 */}
        <Header
          style={{
            padding: isMobile ? '0 16px' : '0 24px',
            background: '#141414',
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {/* 移動端選單按鈕 */}
            {isMobile && (
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setMobileDrawerOpen(true)}
                style={{ fontSize: 20 }}
              />
            )}

            {/* 頁面標題 (桌面版顯示) */}
            {isDesktop && (
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 500 }}>
                {(() => {
                  // 查找當前路徑對應的選單項目（包含巢狀子選單）
                  for (const item of menuItems) {
                    if (item?.key === location.pathname) {
                      return (item as any).label;
                    }
                    // 檢查子選單
                    if ((item as any)?.children) {
                      const child = (item as any).children.find((c: any) => c.key === location.pathname);
                      if (child) {
                        return child.label;
                      }
                    }
                  }
                  return t('common:nav.dashboard');
                })()}
              </h2>
            )}
          </div>

          {/* 使用者資訊區 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 16 }}>
            {!isMobile && organization && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}>
                <BankOutlined style={{ color: token.colorPrimary }} />
                <span style={{ fontWeight: 500, color: token.colorTextSecondary }}>
                  {organization.org_name}
                </span>
              </div>
            )}
            {!isMobile && user && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <UserOutlined />
                <span style={{ fontWeight: 500 }}>{user.full_name || user.username}</span>
                <Tag color={getRoleDisplay(user.role).color}>
                  {getRoleDisplay(user.role).label}
                </Tag>
              </div>
            )}
            {/* 語言切換器 - 所有裝置都顯示 */}
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <LanguageSwitcher />
            </div>
            <Button
              type="text"
              icon={<KeyOutlined />}
              onClick={() => setChangePasswordModalOpen(true)}
            >
              {!isMobile && t('common:actions.changePassword')}
            </Button>
            <Button
              type="text"
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              danger
            >
              {!isMobile && t('common:actions.logout')}
            </Button>
          </div>
        </Header>

        {/* 修改密碼 Modal */}
        <ChangePasswordModal
          open={changePasswordModalOpen}
          onCancel={() => setChangePasswordModalOpen(false)}
          onSuccess={() => {
            // 修改密碼成功後可以選擇登出或繼續使用
          }}
        />


        {/* 內容區域 */}
        <Content
          style={{
            margin: isMobile ? '16px' : '24px',
            padding: isMobile ? '16px' : '24px',
            background: '#051326',
            borderRadius: token.borderRadiusLG,
            minHeight: 'calc(100vh - 64px - 70px - 48px)', // 減去 header, footer, margin
          }}
        >
          <Outlet />
        </Content>

        {/* 底部版權資訊 */}
        <Footer
          style={{
            textAlign: 'center',
            padding: isMobile ? '12px' : '24px',
            fontSize: isMobile ? 12 : 14,
          }}
        >
          CDM-PDD ©{new Date().getFullYear()} - {t('footer.copyright')}
        </Footer>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
